/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, {
  ComponentProps,
  FC,
  useCallback,
  useLayoutEffect,
  useState,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { IRootState } from 'store';
import Title from 'components/common/Title';
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import { StripeCardNumberElement } from '@stripe/stripe-js';
import { AxiosResponse } from 'axios';
import {
  getAvailableCards,
  getAvailablePlans,
  getReferralInfo,
  setDefaultPaymentMethod,
} from 'actions/payments';
import { awaitTillUserMatchState, setUserData } from 'actions/users';
import {
  AvailablePlan,
  CardPaymentIntent,
  CardSetupIntent,
  ChosenPaymentMethod,
  ComponentCallback,
} from 'reducers/payments';
import { selectAvailableCards } from 'selectors/payments';
import { hideSpinner, showSpinner } from 'actions/spinner';
import api from 'services/api';
import { IUser, ParentUser } from 'types';
import {
  isSubscriptionCancelable,
  isUserSubscriptionStatusActive,
} from 'utils/helpers/payments';
import { useTrackable } from 'utils/hooks/useTrackable';
import BillingPlans from './billingPlans';
import CancelSubscription from './cancelSubscription';
import Subscribing from './subscribing';
import styles from './index.module.scss';

function Alert(props: AlertProps) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const mstp = (state: IRootState) => state.anetaPack;

type Props = ReturnType<typeof mstp>;

const Billing: FC<Props> = () => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const availableCards = useSelector(selectAvailableCards);
  const user = useSelector(
    (state: IRootState) => state.users.userData
  ) as ParentUser;

  const [alertMessage, setAlertMessage] = useState<
    | ({ message: string } & Pick<ComponentProps<typeof Alert>, 'severity'>)
    | null
  >(null);
  const [selectedPlan, setSelectedPlan] = useState<AvailablePlan>();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');

  const handleError = (err?: string) => {
    setError(err || 'Something went wrong. Please try again later.');

    const timeOut = setTimeout(() => {
      setError('');
    }, 6000);

    return () => clearTimeout(timeOut);
  };

  const loadInitialData = useTrackable(
    useCallback(async () => {
      setError('');

      dispatch(showSpinner());

      try {
        await Promise.all([
          dispatch(getAvailablePlans()),
          dispatch(getAvailableCards()),
          dispatch(getReferralInfo()),
        ]);
      } catch (e) {
        handleError(e.message);
      }

      dispatch(hideSpinner());
    }, [dispatch])
  );

  useLayoutEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleAlertClose = () => {
    setAlertMessage(null);
  };

  const showSuccessAlert = (message: string) => {
    setAlertMessage({ message, severity: 'success' });
  };

  const showStripeIsNotLoadedAlert = (): void => {
    setAlertMessage({
      message: 'Page is not fully loaded. Try again in a moment.',
      severity: 'warning',
    });
  };

  const handleChangeStep = (st: number) => () => {
    setStep(st);
  };

  const handlePlanSelect = useCallback(
    async (plan: AvailablePlan) => {
      setSelectedPlan(plan);
      setStep(2);
    },
    [setSelectedPlan]
  );

  const setupNewCard = useCallback(async () => {
    const { data }: AxiosResponse<CardSetupIntent> = await api.post(
      '/payments/setup-intents'
    );

    const result = await stripe!.confirmCardSetup(data.clientSecret as string, {
      payment_method: {
        card: elements!.getElement(
          CardNumberElement
        ) as StripeCardNumberElement,
      },
    });

    if (result.error) {
      throw new Error(result.error.message);
    }

    return result;
  }, [elements, stripe]);

  const setupSubscription = useCallback(
    async (params: { paymentMethodId: string; autoRenewOn: boolean }) => {
      const {
        data,
      }: AxiosResponse<CardPaymentIntent | ParentUser> = await api.post(
        '/payments/subscriptions',
        {
          planId: selectedPlan!.id,
          paymentMethodId: params.paymentMethodId,
          autoRenewOn: params.autoRenewOn,
        }
      );

      if ((data as CardPaymentIntent).clientSecret) {
        const paymentResult = await stripe!.confirmCardPayment(
          (data as CardPaymentIntent).clientSecret
        );

        if (paymentResult.error) {
          throw new Error(paymentResult.error.message);
        }
      }

      await dispatch(awaitTillUserMatchState(isUserSubscriptionStatusActive));
    },
    [dispatch, selectedPlan, stripe]
  );

  const handleCardSelection = useCallback(
    async (
      card: ChosenPaymentMethod,
      options = { refreshCards: true }
    ): Promise<string> => {
      if (card.isNew) {
        const cardSetupResult = await setupNewCard();

        if (options.refreshCards) {
          await dispatch(getAvailableCards());
        }

        return cardSetupResult.setupIntent.payment_method as string;
      }

      if (!card.default) {
        await dispatch(setDefaultPaymentMethod(card));
      }

      return card.id;
    },
    [dispatch, setupNewCard]
  );

  const updateDefaultPaymentMethodIfNeeded = useCallback(
    async (card: ChosenPaymentMethod) => {
      if (!card.default) {
        const selectedCardId = await handleCardSelection(card);
        await dispatch(
          setDefaultPaymentMethod({
            id: selectedCardId,
          })
        );
      }
    },
    [dispatch, handleCardSelection]
  );

  const handleSubscribe = useTrackable<
    ComponentCallback<typeof Subscribing, 'onSubscribe'>
  >(
    useCallback(
      async (params) => {
        if (!stripe || !elements) {
          showStripeIsNotLoadedAlert();
          return;
        }

        try {
          const selectedCardId = await handleCardSelection(params.card, {
            refreshCards: false,
          });
          await setupSubscription({
            paymentMethodId: selectedCardId,
            autoRenewOn: params.autoRenewOn,
          });
          await dispatch(getAvailableCards());

          showSuccessAlert('You have successfully subscribed!');
        } catch (e) {
          // @TODO: optimize it! Add error code on the backend
          if (e.message === 'Request failed with status code 400') {
            handleError('Subscription already created for the user!');
          } else {
            handleError(e.message);
          }
        }
      },
      [stripe, elements, handleCardSelection, dispatch, setupSubscription]
    )
  );

  const handleSubscriptionChange = useTrackable<
    ComponentCallback<typeof Subscribing, 'onChangeSubscription'>
  >(
    useCallback(
      async (params) => {
        if (!stripe || !elements) {
          showStripeIsNotLoadedAlert();
          return;
        }

        try {
          await updateDefaultPaymentMethodIfNeeded(params.card);

          const { data }: AxiosResponse<IUser> = await api.put(
            '/payments/subscriptions',
            {
              planId: selectedPlan!.id,
              autoRenewOn: params.autoRenewOn,
            }
          );

          dispatch(setUserData(data));

          showSuccessAlert('You have successfully changed the subscription!');
        } catch (e) {
          handleError(e.message);
        }
      },
      [
        dispatch,
        elements,
        selectedPlan,
        stripe,
        updateDefaultPaymentMethodIfNeeded,
      ]
    )
  );

  const handleRetryPayment = useTrackable<
    ComponentCallback<typeof Subscribing, 'onRetry'>
  >(
    useCallback(
      async (params) => {
        if (!stripe || !elements) {
          showStripeIsNotLoadedAlert();
          return;
        }

        try {
          await updateDefaultPaymentMethodIfNeeded(params.card);

          const {
            data,
          }: AxiosResponse<CardSetupIntent | undefined> = await api.post(
            '/payments/retry-payment'
          );

          if (data) {
            const paymentResponse = await stripe.confirmCardPayment(
              data.clientSecret
            );

            if (paymentResponse.error) {
              throw new Error(paymentResponse.error.message);
            }
          }

          await dispatch(
            awaitTillUserMatchState(isUserSubscriptionStatusActive)
          );

          showSuccessAlert('You have successfully renewed your subscription');
        } catch (e) {
          handleError(e.message);
        }
      },
      [dispatch, elements, stripe, updateDefaultPaymentMethodIfNeeded]
    )
  );

  const getTitle = () => {
    let res;
    if (step === 1) {
      res = 'Plans Details and Billing';
    }
    if (step === 2) {
      res = 'Yay! You are subscribing to Aneta!';
    }
    if (step === 3) {
      res = 'We are sad you want to go';
    }
    return res;
  };

  const handleCancel = useTrackable(
    useCallback(async () => {
      try {
        await api.delete('/payments/subscriptions');
        await dispatch(
          awaitTillUserMatchState(
            ({ subscriptionStatus }) =>
              !isSubscriptionCancelable(subscriptionStatus)
          )
        );

        showSuccessAlert('The subscription has been canceled!');
      } catch (e) {
        handleError(e.response.data?.message[0]);
      }
    }, [dispatch])
  );

  return !loadInitialData.completed ? (
    <></>
  ) : (
    <div className={styles.billingWrapper}>
      <Snackbar
        open={!!alertMessage}
        autoHideDuration={3000}
        onClose={handleAlertClose}
      >
        <Alert onClose={handleAlertClose} severity={alertMessage?.severity}>
          {alertMessage?.message}
        </Alert>
      </Snackbar>
      <Title>{getTitle()}</Title>
      {step === 1 && (
        <BillingPlans
          selectPlan={handlePlanSelect}
          subscriptionPlanId={user.subscriptionPlanId}
          subscriptionStatus={user.subscriptionStatus}
          openCancelPage={handleChangeStep(3)}
        />
      )}
      {step === 2 && (
        <Subscribing
          availableCards={availableCards}
          selectedPlan={selectedPlan}
          currentPlanId={user.subscriptionPlanId}
          subscriptionStatus={user.subscriptionStatus}
          onSubscribe={handleSubscribe}
          onChangeSubscription={handleSubscriptionChange}
          onRetry={handleRetryPayment}
          onBack={handleChangeStep(1)}
          currentSubscriptionEnd={user.subscriptionExpiration}
        />
      )}
      {step === 3 && (
        <CancelSubscription
          subscriptionStatus={user.subscriptionStatus}
          currentPlanId={user.subscriptionPlanId}
          currentSubscriptionEnd={user.subscriptionExpiration}
          onBack={handleChangeStep(1)}
          onCancel={handleCancel}
        />
      )}
      {error !== '' && (
        <span
          style={{
            alignSelf: 'center',
            marginTop: '20px',
            // marginLeft: '100px',
          }}
          className="error-message"
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default connect(mstp)(Billing);
